import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { WireApiService } from '@frontend/shared';
import { TranslateService } from '@ngx-translate/core';
import { ErrorResponse, Workflow, WorkflowPostRequest, WorkflowPutRequest } from '@clientwire/api';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class WorkflowsService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService);
  readonly #toastr = inject(ToastrService);

  readonly loading = signal(false);
  readonly error: WritableSignal<string | null> = signal(null);
  readonly workflows: WritableSignal<Workflow[] | null> = signal([]);

  loadWorkflows(): void {
    this.loading.set(true);
    this.error.set(null);

    this.#wireApiService.workflowsApi
      .listWorkflows()
      .then((response) => {
        this.workflows.set(response.workflows || []);
      })
      .catch(async (error) => {
        console.error('Error loading workflows:', error);

        // Extract error message from backend response
        let errorMessage = this.#translateService.instant('SETTINGS.WORKFLOWS.ERROR_LOADING');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.error.set(errorMessage);
      });
    this.loading.set(false);
  }

  createWorkflow(workflowData: any): void {
    this.loading.set(true);
    const workflowRequest: WorkflowPostRequest = {
      conversationTypeId: workflowData.conversationTypeId,
      trigger: workflowData.trigger,
    };

    this.#wireApiService.workflowsApi
      .createWorkflow({
        workflowPostRequest: workflowRequest,
      })
      .then(() => {
        this.loadWorkflows();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_ADDED_MESSAGE'),
          this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_ADDED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error creating workflow:', error);

        let errorMessage = this.#translateService.instant(
          'SETTINGS.WORKFLOWS.ERROR_ADDING_MESSAGE'
        );

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(
          errorMessage,
          this.#translateService.instant('SETTINGS.WORKFLOWS.ERROR_ADDING_TITLE')
        );
      });

    this.loading.set(false);
  }

  updateWorkflow(workflowId: string, workflowData: any, enabled: boolean): void {
    this.loading.set(true);
    const workflowRequest: WorkflowPutRequest = {
      conversationTypeId: workflowData.conversationTypeId,
      trigger: workflowData.trigger,
      enabled: enabled,
    };

    this.#wireApiService.workflowsApi
      .updateWorkflow({
        workflowId: workflowId,
        workflowPutRequest: workflowRequest,
      })
      .then(() => {
        this.loadWorkflows();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_UPDATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_UPDATED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error updating workflow:', error);

        let errorMessage = this.#translateService.instant(
          'SETTINGS.WORKFLOWS.ERROR_UPDATING_MESSAGE'
        );

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(
          errorMessage,
          this.#translateService.instant('SETTINGS.WORKFLOWS.ERROR_UPDATING_TITLE')
        );
      });

    this.loading.set(false);
  }

  deleteWorkflow(id: string): void {
    this.loading.set(true);
    this.#wireApiService.workflowsApi
      .deleteWorkflow({ workflowId: id })
      .then(() => {
        this.loadWorkflows();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_DELETED_MESSAGE'),
          this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_DELETED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error deleting workflow:', error);

        // Extract error message from backend response
        let errorMessage = this.#translateService.instant(
          'SETTINGS.WORKFLOWS.ERROR_DELETING_MESSAGE'
        );
        const errorTitle = this.#translateService.instant(
          'SETTINGS.WORKFLOWS.ERROR_DELETING_TITLE'
        );

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(errorMessage, errorTitle);
      });

    this.loading.set(false);
  }

  toggleWorkflowEnabled(workflowId: string, enabled: boolean): void {
    this.loading.set(true);

    this.#wireApiService.workflowsApi
      .patchWorkflow({
        workflowId: workflowId,
        requestBody: { enabled: enabled }
      })
      .then(() => {
        // Update the workflow in the local array
        const currentWorkflows = this.workflows();
        if (currentWorkflows) {
          const updatedWorkflows = currentWorkflows.map(workflow => {
            if (workflow.id === workflowId) {
              return { ...workflow, enabled: enabled };
            }
            return workflow;
          });
          this.workflows.set(updatedWorkflows);
        }

        const message = enabled
          ? this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_ENABLED_MESSAGE')
          : this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_DISABLED_MESSAGE');

        const title = this.#translateService.instant('SETTINGS.WORKFLOWS.WORKFLOW_UPDATED_TITLE');

        this.#toastr.success(message, title);
      })
      .catch(async (error: any) => {
        console.error('Error toggling workflow status:', error);

        let errorMessage = this.#translateService.instant(
          'SETTINGS.WORKFLOWS.ERROR_UPDATING_MESSAGE'
        );

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(
          errorMessage,
          this.#translateService.instant('SETTINGS.WORKFLOWS.ERROR_UPDATING_TITLE')
        );
      })
      .finally(() => {
        this.loading.set(false);
      });
  }
}
